import React from 'react';

import Button from '@/components/Button';
import { teal900 } from '@/theme/colors';

import type { Expert } from '../../types';
import { getFirstName } from '../../utils';
import { useStyles } from './ConfirmationScreen.styles';

interface ConfirmationScreenProps {
  onComplete: () => void;
  onContinue?: () => void;
  expert: Expert;
  isNewSignup?: boolean;
}

export function ConfirmationScreen({
  onComplete,
  onContinue,
  expert,
  isNewSignup = true,
}: ConfirmationScreenProps): JSX.Element {
  const s = useStyles();

  const handleContinue = () => {
    onComplete(); // Close modal first
    onContinue?.(); // Then navigate (if callback provided)
  };

  const firstName = getFirstName(expert.name);

  return (
    <>
      <div className={s.confirmationContent}>
        <div className={s.confirmationIcon}>
          <svg width="32" height="32" viewBox="0 0 20 20" fill={teal900}>
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h3 className={s.confirmationTitle}>Request Submitted!</h3>
        <p className={s.confirmationSubtitle}>
          Your consultation request has been sent to {expert.name}.
        </p>

        <div className={s.whatsNextBox}>
          <h4 className={s.whatsNextTitle}>What&apos;s next?</h4>
          <ul className={s.whatsNextList}>
            <li className={s.whatsNextItem}>
              <span className={s.whatsNextCheckmark}>✓</span>
              <span>{firstName} will review your request within 24 hours</span>
            </li>
            <li className={s.whatsNextItem}>
              <span className={s.whatsNextCheckmark}>✓</span>
              <span>Once confirmed, you&apos;ll receive a calendar invite</span>
            </li>
            <li className={s.whatsNextItem}>
              <span className={s.whatsNextCheckmark}>✓</span>
              <span>Your card will be charged after the call</span>
            </li>
          </ul>
        </div>

        {isNewSignup && (
          <div className={s.trialStartedBanner}>
            <p className={s.trialStartedText}>
              <strong>Your 14-day trial has started!</strong> Explore more experts during your
              trial.
            </p>
          </div>
        )}
      </div>

      <div className={s.confirmationFooter}>
        <Button
          color="teal"
          size="normal"
          onClick={handleContinue}
          classes={{ root: s.dashboardButton }}
        >
          {isNewSignup ? 'Go to Dashboard' : 'Continue'}
        </Button>
      </div>
    </>
  );
}

export default ConfirmationScreen;
